import React, { useState } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { Mail, Phone, MapPin, Send, CheckCircle2 } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { PROFILE, IMAGES } from '../mock/data';

const API = process.env.REACT_APP_BACKEND_URL;
const MATTERS = ['immigration', 'family', 'retainr', 'media', 'other'];
const EMPTY = { name: '', email: '', phone: '', matter: 'immigration', message: '', website: '' };

const Field = ({ label, children, testId }) => (
  <label className="block" data-testid={testId}>
    <span className="block text-[10px] tracking-[0.4em] uppercase text-[#8a7a54] mb-2">{label}</span>
    {children}
  </label>
);

const ContactPage = () => {
  const { t } = useLanguage();
  const [form, setForm] = useState(EMPTY);
  const [status, setStatus] = useState('idle');

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setStatus('sending');
    try {
      await axios.post(`${API}/api/contact`, form);
      setStatus('success');
    } catch (_) {
      setStatus('error');
    }
  };

  return (
    <section className="section relative paper-texture min-h-screen" style={{ paddingTop: '150px' }} data-testid="contact-page">
      <div className="container-custom">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: 'easeOut' }}
            className="lg:col-span-5"
          >
            <div className="label-small mb-4">{t.contact.label}</div>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl text-[#F0EDE8] leading-[1.05]">
              {t.contact.heading}
            </h1>
            <div className="gold-rule mt-6 mb-6" />
            <p className="text-[#DCCFAD] text-base md:text-lg leading-relaxed">{t.contact.subheading}</p>

            <div className="mt-10 portrait-frame">
              <div className="chiaroscuro relative overflow-hidden" style={{ aspectRatio: '4/5' }}>
                <img src={IMAGES.office} alt="KC Redding reviewing documents with clients" className="w-full h-full object-cover" style={{ objectPosition: 'center 30%' }} />
              </div>
            </div>

            <div className="mt-10 space-y-6">
              <div className="text-[10px] tracking-[0.4em] uppercase text-[#8a7a54]">{t.contact.directLabel}</div>
              <a href={`mailto:${PROFILE.email}`} className="flex items-center gap-3 text-[#F0EDE8] hover:text-[#C4A35A] transition-colors" data-testid="contact-email-link">
                <Mail size={16} className="text-[#C4A35A]" />
                <span className="font-heading text-lg">{PROFILE.email}</span>
              </a>
              <a href={`tel:${PROFILE.phone.replace(/[^0-9+]/g, '')}`} className="flex items-center gap-3 text-[#F0EDE8] hover:text-[#C4A35A] transition-colors" data-testid="contact-phone-link">
                <Phone size={16} className="text-[#C4A35A]" />
                <span className="font-heading text-lg">{PROFILE.phone}</span>
              </a>
              <div className="flex items-center gap-3 text-[#F0EDE8]">
                <MapPin size={16} className="text-[#C4A35A]" />
                <span className="font-heading text-lg">{PROFILE.location}</span>
              </div>
              <div className="text-[#8a7a54] text-sm italic font-heading">{t.contact.responseNote}</div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.15, ease: 'easeOut' }}
            className="lg:col-span-7"
          >
            <div className="relative border border-[rgba(196,163,90,0.3)] bg-[#0f1422] p-8 md:p-12">
              <span className="absolute top-0 left-0 w-6 h-6 border-t border-l border-[#C4A35A]" />
              <span className="absolute top-0 right-0 w-6 h-6 border-t border-r border-[#C4A35A]" />
              <span className="absolute bottom-0 left-0 w-6 h-6 border-b border-l border-[#C4A35A]" />
              <span className="absolute bottom-0 right-0 w-6 h-6 border-b border-r border-[#C4A35A]" />

              {status === 'success' ? (
                <div className="text-center py-16" data-testid="contact-success">
                  <CheckCircle2 size={44} className="text-[#C4A35A] mx-auto mb-6" />
                  <h2 className="font-heading text-3xl text-[#F0EDE8]">{t.contact.successTitle}</h2>
                  <div className="gold-rule mx-auto my-6" />
                  <p className="text-[#DCCFAD]">{t.contact.successBody}</p>
                  <button onClick={() => { setForm(EMPTY); setStatus('idle'); }} className="btn-outline-gold mt-10" data-testid="contact-send-another">
                    {t.contact.another}
                  </button>
                </div>
              ) : (
                <form onSubmit={submit} className="space-y-7" data-testid="contact-form">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-7">
                    <Field label={t.contact.name} testId="contact-field-name">
                      <input required className="field-gold" value={form.name} onChange={set('name')} data-testid="contact-input-name" maxLength={120} />
                    </Field>
                    <Field label={t.contact.email} testId="contact-field-email">
                      <input required type="email" className="field-gold" value={form.email} onChange={set('email')} data-testid="contact-input-email" />
                    </Field>
                    <Field label={t.contact.phone} testId="contact-field-phone">
                      <input type="tel" className="field-gold" value={form.phone} onChange={set('phone')} data-testid="contact-input-phone" maxLength={40} />
                    </Field>
                    <Field label={t.contact.matter} testId="contact-field-matter">
                      <select className="field-gold" value={form.matter} onChange={set('matter')} data-testid="contact-select-matter">
                        {MATTERS.map((m) => (
                          <option key={m} value={m}>{t.contact.matters[m]}</option>
                        ))}
                      </select>
                    </Field>
                  </div>
                  <Field label={t.contact.message} testId="contact-field-message">
                    <textarea
                      required
                      rows={7}
                      minLength={10}
                      maxLength={5000}
                      className="field-gold resize-none"
                      placeholder={t.contact.messagePlaceholder}
                      value={form.message}
                      onChange={set('message')}
                      data-testid="contact-input-message"
                    />
                  </Field>
                  <input type="text" value={form.website} onChange={set('website')} tabIndex={-1} autoComplete="off" className="hidden" aria-hidden="true" />

                  {status === 'error' && (
                    <div className="text-[#E3C884] text-sm border border-[rgba(196,163,90,0.4)] bg-[rgba(196,163,90,0.06)] px-4 py-3" data-testid="contact-error">
                      {t.contact.error}
                    </div>
                  )}

                  <button type="submit" disabled={status === 'sending'} className="btn-gold w-full md:w-auto" data-testid="contact-submit-button">
                    <Send size={14} />
                    {status === 'sending' ? t.contact.sending : t.contact.send}
                  </button>
                </form>
              )}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default ContactPage;
